import React, { useEffect, useState } from "react";
import { Search, Plus, Link2, Building2, Calendar, MapPin, CreditCard } from "lucide-react";
import useRequest from "../../../components/hook/use-req";
import Pagination from "../../../components/pagination";
import ReuseableDrawer from "../../../components/resuable-drawer";
import { formatCurrency } from "../../../utils/function";
import {
  ResellerDetailResponse,
  ResellerOrg,
  ResellerSummary,
  ResellersListResponse,
  formatResellerDate,
  getActiveStyle,
  getResellerInitials,
} from "./types";
import CreateResellerModal from "./create-reseller-modal";
import AttachOrgModal from "./attach-org-modal";

// "View Reseller" — GET /admin/resellers/{id}. A drawer, matching the
// Businesses feature's own click-a-row-to-inspect convention.
const ResellerDetailsDrawer: React.FC<{
  resellerId: string | null;
  onClose: () => void;
  onOrgAttached: () => void;
}> = ({ resellerId, onClose, onOrgAttached }) => {
  const token = localStorage.getItem("authToken");
  const [detail, setDetail] = useState<ResellerDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [isAttachOpen, setIsAttachOpen] = useState(false);

  const { makeRequest: getResellerDetail } = useRequest("/admin/resellers", "GET", {
    Authorization: `Bearer ${token}`,
  });

  const fetchDetail = async () => {
    if (!resellerId) return;
    setLoading(true);
    const [response] = (await getResellerDetail(undefined, undefined, resellerId)) as [ResellerDetailResponse, number];
    if (response?.status) setDetail(response);
    setLoading(false);
  };

  useEffect(() => {
    setDetail(null);
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resellerId]);

  const handleAttached = () => {
    fetchDetail();
    onOrgAttached();
  };

  return (
    <>
      <ReuseableDrawer isOpen={!!resellerId} onClose={onClose} title="Reseller Details" initialWidth="42%">
        {loading && !detail ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-7 h-7 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : detail ? (
          <div className="p-5 space-y-6">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-500/15 flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                    {getResellerInitials(detail.organization.name)}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="text-base font-bold text-gray-900 dark:text-gray-100 truncate">{detail.organization.name}</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${getActiveStyle(detail.organization.is_active)}`}>
                      {detail.organization.is_active ? "active" : "inactive"}
                    </span>
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 capitalize">
                      {detail.reseller.business_type}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="min-w-0">
                <p className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500 uppercase tracking-wide mb-1">Address</p>
                <p className="text-sm font-medium text-gray-800 dark:text-gray-100 break-words flex items-center gap-1.5">
                  <MapPin size={13} className="text-[#7B7B7B] font-semibold dark:text-gray-500 shrink-0" /> {detail.reseller.business_address || "—"}
                </p>
              </div>
              <div className="min-w-0">
                <p className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500 uppercase tracking-wide mb-1">Preferred Payment</p>
                <p className="text-sm font-medium text-gray-800 dark:text-gray-100 break-words flex items-center gap-1.5 capitalize">
                  <CreditCard size={13} className="text-[#7B7B7B] font-semibold dark:text-gray-500 shrink-0" />
                  {detail.reseller.preferred_payment?.replace(/_/g, " ") || "—"}
                </p>
              </div>
              <div className="min-w-0">
                <p className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500 uppercase tracking-wide mb-1">Expected Daily Volume</p>
                <p className="text-sm font-medium text-gray-800 dark:text-gray-100">{detail.reseller.expected_daily_volume || "—"}</p>
              </div>
              <div className="min-w-0">
                <p className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500 uppercase tracking-wide mb-1">Created</p>
                <p className="text-sm font-medium text-gray-800 dark:text-gray-100 break-words flex items-center gap-1.5">
                  <Calendar size={13} className="text-[#7B7B7B] font-semibold dark:text-gray-500 shrink-0" /> {formatResellerDate(detail.organization.created_at)}
                </p>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Wallet</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 dark:bg-gray-800/60 rounded-lg p-3">
                  <p className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500">Reseller's Own Balance</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {formatCurrency(detail.organization.wallet_balance, detail.organization.currency, "symbol")}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/60 rounded-lg p-3">
                  <p className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500 mb-1">Total Across All Orgs</p>
                  <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                    {Object.entries(detail.total_wallet_balance_by_currency).map(([cur, amt]) => (
                      <span key={cur} className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {formatCurrency(amt, cur, "symbol")}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Attached Organizations {detail.organizations.length > 0 && `(${detail.organizations.length})`}
                </p>
                <button
                  type="button"
                  onClick={() => setIsAttachOpen(true)}
                  className="flex items-center gap-1 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                >
                  <Link2 size={12} /> Attach Organization
                </button>
              </div>
              {detail.organizations.length === 0 ? (
                <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">No organizations attached yet.</p>
              ) : (
                <div className="border border-gray-100 dark:border-gray-800 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[480px] text-left border-collapse">
                      <thead>
                        <tr className="border-b border-gray-100 dark:border-gray-800">
                          {["Organization", "Type", "Wallet Balance", "Status", "Created"].map((h) => (
                            <th key={h} className="px-3 py-2 text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {detail.organizations.map((org: ResellerOrg) => (
                          <tr key={org.id} className="border-b border-gray-50 dark:border-gray-800/60 last:border-b-0">
                            <td className="px-3 py-2.5 text-sm text-gray-800 dark:text-gray-100 truncate max-w-[160px]">{org.name}</td>
                            <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 capitalize whitespace-nowrap">{org.type || "—"}</td>
                            <td className="px-3 py-2.5 text-sm text-gray-800 dark:text-gray-100 whitespace-nowrap">
                              {formatCurrency(org.wallet_balance, org.currency, "symbol")}
                            </td>
                            <td className="px-3 py-2.5 whitespace-nowrap">
                              <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${getActiveStyle(org.is_active)}`}>
                                {org.is_active ? "active" : "inactive"}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 whitespace-nowrap">{formatResellerDate(org.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="p-10 text-center text-sm text-gray-500 dark:text-gray-400">Couldn't load this reseller.</div>
        )}
      </ReuseableDrawer>

      {detail && (
        <AttachOrgModal
          isOpen={isAttachOpen}
          onClose={() => setIsAttachOpen(false)}
          resellerId={detail.reseller.id}
          onAttached={handleAttached}
        />
      )}
    </>
  );
};

// Global Admin's Resellers — GET /admin/resellers, fully backend-driven
// (list, detail, create, attach-organization), no hardcoded/sample data.
const GlobalAdminResellers: React.FC = () => {
  const token = localStorage.getItem("authToken");
  const [data, setData] = useState<ResellersListResponse | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedResellerId, setSelectedResellerId] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const { makeRequest: getResellers } = useRequest("/admin/resellers", "GET", {
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const fetchData = async () => {
    if (page === 1) setInitialLoading(true);
    else setIsPaginationLoading(true);
    const params: Record<string, unknown> = { page, limit: pageSize };
    if (debouncedSearch) params.search = debouncedSearch;
    const [response] = (await getResellers(undefined, params)) as [ResellersListResponse, number];
    if (response?.status) setData(response);
    setInitialLoading(false);
    setIsPaginationLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearch]);

  const resellers = data?.resellers || [];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-6">
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">Resellers</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {data ? `${data.total.toLocaleString()} resellers` : "Manage resellers and the organizations attached to them."}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsCreateOpen(true)}
            className="flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors shrink-0"
          >
            <Plus size={16} /> Create Reseller
          </button>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 sm:p-5">
          <div className="relative mb-4 max-w-sm">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search resellers"
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-500/20 focus:border-blue-400"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Reseller</th>
                  <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Business Type</th>
                  <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Daily Volume</th>
                  <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Orgs Attached</th>
                  <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Status</th>
                  <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Created</th>
                </tr>
              </thead>
              <tbody>
                {initialLoading || isPaginationLoading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-10">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">Loading…</span>
                      </div>
                    </td>
                  </tr>
                ) : resellers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">
                      {debouncedSearch ? "No resellers match your search." : "No resellers yet."}
                    </td>
                  </tr>
                ) : (
                  resellers.map((r: ResellerSummary) => (
                    <tr
                      key={r.id}
                      onClick={() => setSelectedResellerId(r.id)}
                      className="border-b border-gray-50 dark:border-gray-800 hover:bg-blue-50/30 dark:hover:bg-blue-500/10 transition-colors cursor-pointer group"
                    >
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-500/15 flex items-center justify-center shrink-0">
                            <span className="text-xs font-bold text-blue-600 dark:text-blue-400">{getResellerInitials(r.organization_name)}</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{r.organization_name}</p>
                        </div>
                      </td>
                      <td className="py-3 px-2 text-sm text-gray-600 dark:text-gray-300 capitalize whitespace-nowrap">{r.business_type || "—"}</td>
                      <td className="py-3 px-2 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">{r.expected_daily_volume || "—"}</td>
                      <td className="py-3 px-2 text-sm text-gray-800 dark:text-gray-100 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                          <Building2 size={12} className="text-[#7B7B7B] font-semibold dark:text-gray-500" /> {r.organization_count}
                        </span>
                      </td>
                      <td className="py-3 px-2">
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${getActiveStyle(r.is_active)}`}>
                          {r.is_active ? "active" : "inactive"}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">{formatResellerDate(r.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="pt-2">
            <Pagination
              currentPage={page}
              totalPages={data?.pages ?? 1}
              pageSize={pageSize}
              isLoading={isPaginationLoading}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <ResellerDetailsDrawer
        resellerId={selectedResellerId}
        onClose={() => setSelectedResellerId(null)}
        onOrgAttached={fetchData}
      />
      <CreateResellerModal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} onCreated={fetchData} />
    </div>
  );
};

export default GlobalAdminResellers;
